import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MinigameViewProps } from '../../types/minigame';

// Load Balancer (level 30) — the management rung. Bursts arrive one at a time and you choose which
// node absorbs each. A burst occupies its node for a duration, so this is fitting intervals rather
// than filling buckets; and nodes accumulate HEAT while they sit more than about 40% loaded, so
// always taking the tightest fit cooks the small ones. Spread, or watch them melt.
//
// ON TIMERS, because this bit has bitten before: the only ticker here is a single interval keyed on
// NOTHING, and everything time-dependent is derived from it plus the last state the server sent.
// A ticker whose dependencies include the polled state gets torn down and rebuilt on every poll and
// never fires — that is exactly how Log Scrubber ended up showing one line and jumping to the end.

interface Burst {
  index: number;
  size: number;
  holdMs: number;
  arriveAtMs: number;
  deadlineMs: number;
}

interface Placement {
  node: number;
  size: number;
  startMs: number;
  endMs: number;
}

interface Challenge {
  capacities: number[];
  totalBursts: number;
  intervalMs: number;
  lookahead: number;
  purges: number;
  purgesGranted: number;
  purgeNote: string | null;
  integrity: number;
  maxIntegrity: number;
  spillCostPerUnit: number;
  heat: number[];
  /** When each node melted, in flood-ms, or -1 while it still stands. */
  melted: number[];
  meltAt: number;
  /** Below this occupancy a node cools; above it, it builds heat. */
  coolAtPercent: number;
  started: boolean;
  startedAtMs: number;
  cursor: number;
  spilled: number[];
  visible: Burst[];
  placements: Placement[];
  timeLimitSec: number;
}

const POLL_MS = 400;

export default function LoadBalancerView({
  challenge, onAct, onSubmit, setPendingSubmission, startedAgoMs, busy,
}: MinigameViewProps<Challenge, Challenge>) {
  const [live, setLive] = useState<Challenge | null>(challenge ?? null);
  const [elapsed, setElapsed] = useState(0);
  const [note, setNote] = useState<string | null>(null);
  // A click is an INTENT, not a command, and it names the burst it was made for. The player's clock
  // and the server's differ by a round trip, so a click made just as a burst lands used to be
  // dropped on the floor - the button was disabled on the client's own reckoning - and the two or
  // three extra clicks that followed landed on the NEXT burst. Held here until it can be applied.
  const [intent, setIntent] = useState<{ burst: number; node: number } | null>(null);
  const pollingRef = useRef(false);
  const sendingRef = useRef(false);

  // Anchored during render, pulled back by however long the attempt has been running — see the
  // note in PortScanSweepView.
  const anchorRef = useRef<{ key: unknown; at: number }>({ key: challenge, at: Date.now() - startedAgoMs });
  if (anchorRef.current.key !== challenge) anchorRef.current = { key: challenge, at: Date.now() - startedAgoMs };

  useEffect(() => {
    setLive(challenge ?? null);
    setNote(null);
    setIntent(null);
    sendingRef.current = false;
    setPendingSubmission({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challenge]);

  // The one and only ticker. Deps are empty on purpose: nothing the server sends may be allowed to
  // reset it.
  useEffect(() => {
    const id = setInterval(() => setElapsed(Date.now() - anchorRef.current.at), 100);
    return () => clearInterval(id);
  }, []);

  const started = live?.started ?? false;
  const capacities = useMemo(() => live?.capacities ?? [], [live]);
  const placements = useMemo(() => live?.placements ?? [], [live]);
  const visible = useMemo(() => live?.visible ?? [], [live]);
  const melted = useMemo(() => live?.melted ?? [], [live]);
  const heat = useMemo(() => live?.heat ?? [], [live]);
  const done = started && visible.length === 0;

  // Flood-relative time, the frame everything the server sends is expressed in.
  const floodMs = started ? Math.max(0, elapsed - (live?.startedAtMs ?? 0)) : 0;

  const absorb = useCallback((next: Challenge) => setLive(next), []);

  useEffect(() => {
    if (!started || done) return;
    const id = setInterval(() => {
      // Not while the player's own action is in flight: its response carries the same state, and a
      // poll started alongside it is the one that finds the session already consumed when that
      // action turns out to have ended the round.
      if (pollingRef.current || sendingRef.current) return;
      pollingRef.current = true;
      void (async () => {
        try {
          const next = await onAct({ kind: 'poll' });
          if (next) absorb(next);
        } finally {
          pollingRef.current = false;
        }
      })();
    }, POLL_MS);
    return () => clearInterval(id);
  }, [started, done, onAct, absorb]);

  /** What a node is carrying right now — the same rule the server grades with. */
  const loadOf = (node: number) =>
    placements.filter(p => p.node === node && p.startMs <= floodMs && floodMs < p.endMs)
              .reduce((sum, p) => sum + p.size, 0);

  /** When that node next frees up some room. */
  const freesInMs = (node: number) => {
    const ends = placements.filter(p => p.node === node && p.endMs > floodMs).map(p => p.endMs);
    return ends.length === 0 ? null : Math.min(...ends) - floodMs;
  };

  const front = visible.length > 0 ? visible[0] : null;
  const landed = front !== null && floodMs >= front.arriveAtMs;
  const untilSpill = front === null ? 0 : front.deadlineMs - floodMs;

  const send = async (action: unknown) => {
    // Deliberately NOT gated on `busy`. That flag is the host's "a request is in flight", and this
    // view polls three times a second — so gating on it dropped clicks that landed during a poll
    // and made every node button flicker in and out of being disabled. sendingRef is the real
    // re-entrancy guard: one player action at a time, polls not counted.
    if (sendingRef.current) return;
    sendingRef.current = true;
    setNote(null);
    try {
      const next = await onAct(action);
      if (next) absorb(next);
    } finally {
      sendingRef.current = false;
    }
  };

  const begin = () => void send({ kind: 'begin' });

  /** Take the click now; work out when it can actually be sent later. */
  const assign = (node: number) => {
    if (!front) return;
    const name = `NODE-${String.fromCharCode(65 + node)}`;
    if (melted[node] >= 0) { setNote(`${name} has melted`); return; }

    // Refused outright only when it is hopeless: no room, and nothing on that node frees up before
    // this burst spills. A shortage that WILL clear in time is worth holding for - a timing problem
    // is the machine's to solve, not something to make the player re-click.
    if (loadOf(node) + front.size > capacities[node]) {
      const frees = freesInMs(node);
      if (frees === null || frees > untilSpill) {
        setNote(`${name} hasn't the room — ${capacities[node] - loadOf(node)} free, the burst needs ${front.size}`);
        return;
      }
    }
    setNote(null);
    setIntent({ burst: front.index, node });     // a later click on another node simply replaces it
  };

  const purge = (node: number) => void send({ kind: 'purge', node });

  // Send a held intent the moment it becomes applicable. This is a plain effect rather than a timer
  // on purpose: it re-runs on every clock tick and every server update, which is precisely when the
  // answer to "can it go yet?" changes. One request at a time (sendingRef), and a rejection drops
  // the intent rather than retrying, so this can never spin.
  useEffect(() => {
    if (!intent || !started || done || sendingRef.current) return;
    if (!front || front.index !== intent.burst) { setIntent(null); return; }   // that burst has gone
    if (floodMs < front.arriveAtMs) return;                                    // hasn't landed: hold
    if (melted[intent.node] >= 0) {
      setNote(`NODE-${String.fromCharCode(65 + intent.node)} melted before it could take it`);
      setIntent(null);
      return;
    }
    if (loadOf(intent.node) + front.size > capacities[intent.node]) return;    // still full: hold
    setIntent(null);
    void send({ kind: 'assign', node: intent.node, burst: intent.burst });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intent, front, floodMs, started, done, placements, melted]);

  const integrity = live?.integrity ?? 100;
  const maxIntegrity = live?.maxIntegrity ?? 100;
  const row: React.CSSProperties = { fontFamily: 'monospace', fontSize: '0.8rem' };

  return (
    <div style={{ fontFamily: 'monospace' }}>
      {/* Integrity, purges, progress */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap',
        border: '1px solid var(--border-color)', borderRadius: 4, padding: '0.5rem 0.7rem',
        marginBottom: '0.7rem', background: '#0d1117',
      }}>
        <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>integrity</span>
        <div style={{ flex: '1 1 120px', minWidth: 100, height: 8, background: '#111', borderRadius: 4, overflow: 'hidden' }}>
          <div style={{
            width: `${Math.max(0, (integrity / maxIntegrity) * 100)}%`, height: '100%',
            background: integrity > 60 ? '#4ade80' : integrity > 30 ? '#fbbf24' : '#f87171',
            transition: 'width 0.2s',
          }} />
        </div>
        <strong style={{ ...row, color: integrity > 30 ? '#cbd5e1' : '#f87171' }}>{integrity}/{maxIntegrity}</strong>
        {(live?.purgesGranted ?? 0) > 0 && (
          <span style={{ ...row, fontSize: '0.74rem', color: (live?.purges ?? 0) > 0 ? '#60a5fa' : '#6b7280' }}>
            purges {live?.purges ?? 0}/{live?.purgesGranted ?? 0}
          </span>
        )}
        <span style={{ ...row, fontSize: '0.74rem', color: 'var(--text-muted)' }}>
          burst {Math.min((live?.cursor ?? 0) + 1, live?.totalBursts ?? 0)} of {live?.totalBursts ?? 0}
        </span>
      </div>

      {!started ? (
        <div style={{ border: '1px solid #232833', borderRadius: 4, padding: '0.9rem', background: '#0d1117' }}>
          <p style={{ ...row, color: '#cbd5e1', margin: '0 0 0.5rem', lineHeight: 1.6 }}>
            A flood is coming in. Each <strong>burst</strong> has a size and a duration: send it to a
            node and it occupies that much of it until it expires. Place the burst at the front
            before the next one lands, or it <strong>spills</strong> and costs you integrity.
          </p>
          <p style={{ ...row, color: '#cbd5e1', margin: '0 0 0.5rem', lineHeight: 1.6 }}>
            And nodes run <strong style={{ color: '#f87171' }}>hot</strong>: one sitting above{' '}
            {live?.coolAtPercent ?? 42}% load builds heat and cools below it. Let a node reach{' '}
            {live?.meltAt ?? 100} and it <strong>melts</strong> — its traffic is dropped and you lose
            its capacity for the rest of the round. Always taking the tightest fit will cook your
            small nodes. Spread the load.
          </p>
          <p style={{ ...row, color: 'var(--text-muted)', margin: '0 0 0.7rem' }}>
            You can see {live?.lookahead ?? 2} burst{(live?.lookahead ?? 2) === 1 ? '' : 's'} ahead.
            The round clock is already running, but nothing arrives until you begin.
          </p>
          <button className="primary" onClick={begin} disabled={busy} style={{ fontSize: '0.85rem', padding: '0.4rem 1rem' }}>
            Open the valve
          </button>
        </div>
      ) : (
        <>
          {/* The incoming queue. Every row is ALWAYS rendered, at a fixed height, whether a burst
              is waiting or not: this panel used to collapse to one line the moment a burst was
              absorbed and expand again when the next landed, which lifted and dropped the whole
              board several times a round — the node buttons kept sliding out from under the mouse
              exactly when they were needed. Nothing below this may depend on what is in it. */}
          <div style={{
            border: `1px solid ${landed ? 'var(--primary-color)' : '#232833'}`,
            borderRadius: 4, padding: '0.6rem 0.7rem', marginBottom: '0.7rem',
            background: landed ? '#12161d' : '#0d1117', boxSizing: 'border-box',
          }}>
            <div style={{
              display: 'flex', gap: '0.6rem', alignItems: 'baseline',
              height: '1.5rem', whiteSpace: 'nowrap', overflow: 'hidden',
            }}>
              {front === null ? (
                <span style={{ ...row, fontSize: '1.05rem', color: '#4ade80' }}>the flood is over — settling up…</span>
              ) : !landed ? (
                <span style={{ ...row, fontSize: '1.05rem', color: '#4b5563' }}>next burst inbound…</span>
              ) : (
                <>
                  <strong style={{ ...row, fontSize: '1.05rem', color: '#e2e8f0' }}>
                    {front.size} unit{front.size === 1 ? '' : 's'}
                  </strong>
                  <span style={{ ...row, color: 'var(--text-muted)' }}>holds {(front.holdMs / 1000).toFixed(0)}s</span>
                  <span style={{ ...row, color: untilSpill < 1500 ? '#f87171' : 'var(--text-muted)' }}>
                    spills in {(Math.max(0, untilSpill) / 1000).toFixed(1)}s
                  </span>
                </>
              )}
            </div>
            <div style={{ height: 3, background: '#1a1a2e', borderRadius: 2, overflow: 'hidden', marginTop: '0.35rem' }}>
              <div style={{
                width: front !== null && landed
                  ? `${Math.max(0, Math.min(100, (untilSpill / Math.max(1, live?.intervalMs ?? 4500)) * 100))}%`
                  : '0%',
                height: '100%', background: untilSpill < 1500 ? '#f87171' : 'var(--primary-color)',
              }} />
            </div>
            <div style={{
              ...row, color: '#6b7280', fontSize: '0.72rem', marginTop: '0.3rem',
              height: '1rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {visible.length > 1
                ? `then: ${visible.slice(1).map(b => `${b.size}u/${(b.holdMs / 1000).toFixed(0)}s`).join(' · ')}`
                : '\u00a0'}
            </div>
          </div>

          {/* The nodes */}
          <div style={{ display: 'grid', gap: '0.4rem', marginBottom: '0.7rem' }}>
            {capacities.map((cap, n) => {
              const load = loadOf(n);
              const isMelted = melted[n] >= 0;
              const h = heat[n] ?? 0;
              const fits = !isMelted && front !== null && load + front.size <= cap;
              const frees = freesInMs(n);
              const queued = intent?.node === n;
              return (
                <div key={n} style={{
                  border: `1px solid ${isMelted ? '#7f1d1d' : fits && landed ? '#166534' : '#232833'}`,
                  borderRadius: 4, padding: '0.45rem 0.6rem',
                  background: isMelted ? 'rgba(127,29,29,0.12)' : '#0d1117',
                  opacity: isMelted ? 0.6 : 1,
                }}>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    {/* Enabled whenever the node stands. Disabling it on the client's own reading of
                        the clock is what dropped clicks at a burst boundary; anything that cannot be
                        done yet is queued, and anything hopeless says so. */}
                    <button
                      onClick={() => assign(n)}
                      disabled={isMelted}
                      style={{
                        ...row, minWidth: '5.2rem', padding: '0.25rem 0.5rem',
                        outline: queued ? '2px solid var(--primary-color)' : undefined,
                      }}
                      title={isMelted ? 'melted' : fits ? 'send the burst here' : 'no room right now'}
                    >
                      NODE-{String.fromCharCode(65 + n)}
                    </button>
                    <div style={{ flex: '1 1 90px', minWidth: 80, height: 8, background: '#111', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{
                        width: `${cap > 0 ? (load / cap) * 100 : 0}%`, height: '100%',
                        background: isMelted ? '#7f1d1d' : 'var(--primary-color)', transition: 'width 0.2s',
                      }} />
                    </div>
                    <span style={{ ...row, color: 'var(--text-muted)', fontSize: '0.74rem', minWidth: '3.2rem' }}>
                      {load}/{cap}
                    </span>
                    <span style={{
                      ...row, fontSize: '0.7rem', minWidth: '3.8rem',
                      color: h > 75 ? '#f87171' : h > 45 ? '#fbbf24' : '#4ade80',
                    }}>
                      heat {h.toFixed(0)}
                    </span>
                    {/* Present for the whole round once purges are granted, and merely disabled when
                        it can't be used. Mounting it only when the node had traffic made it appear
                        and vanish as nodes filled and drained, re-wrapping the row on a narrow
                        screen and shifting every node below it. */}
                    {(live?.purgesGranted ?? 0) > 0 && (
                      <button
                        onClick={() => purge(n)}
                        disabled={(live?.purges ?? 0) === 0 || isMelted || load === 0}
                        style={{ ...row, fontSize: '0.68rem', padding: '0.1rem 0.4rem' }}
                        title={(live?.purges ?? 0) === 0 ? 'no purges left' : 'Drop everything running here'}
                      >
                        purge
                      </button>
                    )}
                  </div>
                  <div style={{ ...row, fontSize: '0.68rem', color: '#4b5563', marginTop: '0.2rem' }}>
                    {isMelted
                      ? 'MELTED — out for the rest of the round'
                      : queued
                        ? (landed ? 'sending…' : 'QUEUED — goes in when the burst lands')
                        : frees === null ? 'idle' : `frees up in ${(frees / 1000).toFixed(1)}s`}
                  </div>
                </div>
              );
            })}
          </div>

          {/* A reserved slot: a message that came and went used to move the button below it. */}
          <div style={{ height: '1.2rem', marginBottom: '0.5rem', overflow: 'hidden' }}>
            {(note || live?.purgeNote) && (
              <span
                key={note ?? live?.purgeNote}
                className="fx-fade-in"
                style={{ ...row, color: note ? '#fbbf24' : '#60a5fa', fontSize: '0.76rem' }}
              >
                {note ?? live?.purgeNote}
              </span>
            )}
          </div>

          {done && (
            <button
              className="primary"
              onClick={() => onSubmit({})}
              disabled={busy}
              style={{ fontSize: '0.85rem', padding: '0.35rem 0.9rem' }}
            >
              Settle the round
            </button>
          )}
        </>
      )}
    </div>
  );
}
